import { getCurrentDenkerWs } from "@/api/canvas-ws-ref";
import { useMemo } from "react";
import { useCompanionStore } from "@/stores/companion-store";
import { useDirectSessionProgressStore } from "@/stores/direct-session-progress-store";

function isActive(status: string): boolean {
  return status === "running" || status === "waiting_approval";
}

export function useConversationFrameRunningState({
  detachedExecutionId,
  detachedIsRunning,
  executionId,
  preferDetachedRunning,
  runtimeSessionId,
  conversationId,
}: {
  detachedExecutionId?: string;
  detachedIsRunning?: boolean;
  executionId?: string;
  preferDetachedRunning: boolean;
  runtimeSessionId?: string;
  conversationId?: string;
}) {
  const executions = useCompanionStore((state) => state.executions);
  const sessionExecutionId = useCompanionStore((state) => runtimeSessionId
    ? Object.values(state.agentSessions).find((session) => session.runtimeSessionId === runtimeSessionId)?.executionId
    : undefined);
  // A direct-session turn Denker sent has no execution events: it runs while
  // its task shows a live line that named the turn.
  const directProgress = useDirectSessionProgressStore((state) => state.byTask);
  const runningExecutionIds = useMemo(() => {
    const ids = new Set<string>();
    for (const execution of executions.values()) {
      if (isActive(execution.status)) ids.add(execution.id);
    }
    for (const progress of Object.values(directProgress)) {
      if (progress.executionId) ids.add(progress.executionId);
    }
    if (detachedIsRunning && detachedExecutionId) {
      ids.add(detachedExecutionId);
    }
    return ids;
  }, [detachedExecutionId, detachedIsRunning, directProgress, executions]);
  const exactExecutions = Array.from(executions.values()).filter((execution) =>
    conversationId ? execution.conversationId === conversationId
      : runtimeSessionId ? execution.runtimeSessionId === runtimeSessionId
        : execution.id === executionId,
  );
  const activeExecution = exactExecutions.find((execution) => isActive(execution.status));
  const fallbackId = executionId ?? sessionExecutionId;
  const fallbackExecution = fallbackId ? executions.get(fallbackId) : undefined;
  const ownedExecution = activeExecution ?? (fallbackExecution &&
    (!conversationId || !fallbackExecution.conversationId || fallbackExecution.conversationId === conversationId)
    ? fallbackExecution : undefined);
  const storeIsRunning = !!ownedExecution && isActive(ownedExecution.status);
  const onStop = () => {
    const targetId = ownedExecution?.id ?? (preferDetachedRunning ? detachedExecutionId : undefined);
    if (targetId) getCurrentDenkerWs()?.stopExecution(targetId);
  };
  const isRunning = preferDetachedRunning
    ? (detachedIsRunning ?? storeIsRunning)
    : storeIsRunning;

  return { isRunning, runningExecutionIds, onStop };
}
